import { useEffect, useRef, type RefObject } from "react";

/** Qué fracción del desplazamiento recorre el fondo. Menos es más. */
const SHIFT = 0.12;

/**
 * Cuánto sobresale el fondo por arriba y por abajo de la portada, en
 * fracción de su alto. Es el margen del que come el parallax: mientras el
 * desplazamiento no lo supere, el borde del vídeo no puede asomar.
 */
export const OVERSCAN = 0.14;

/**
 * Parallax del fondo de la portada.
 *
 * ── Por qué `transform` y no `top` ni `background-position` ──────────
 * `transform` es una propiedad que el compositor resuelve él solo: la
 * capa ya está subida a la GPU y moverla no obliga al navegador a
 * recalcular geometría ni a repintar un solo píxel. Cambiar `top` dispara
 * layout + paint + composite en CADA fotograma del desplazamiento, con un
 * vídeo de 720×720 descodificándose al mismo tiempo; en un teléfono eso
 * es la diferencia entre 60 fps y un desplazamiento a tirones.
 *
 * ── Por qué una `ref` y no estado de React ───────────────────────────
 * Guardar la posición en `useState` obligaría a re-renderizar el árbol de
 * la portada sesenta veces por segundo para escribir un número en un
 * `style`. Aquí se escribe directamente en el nodo: React no se entera y
 * no tiene por qué.
 *
 * ── El tope no es decorativo ─────────────────────────────────────────
 * El desplazamiento se limita a `OVERSCAN` del alto de la portada, que es
 * justo lo que el fondo sobresale por arriba. Sin ese tope, con la
 * portada ya fuera de pantalla el fondo se habría bajado más de lo que
 * sobra y se vería la franja vacía por encima.
 */
export function useHeroParallax(enabled: boolean): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const layer = ref.current;
    if (!layer) return;

    // Sin movimiento el fondo se queda donde lo puso el CSS. Se limpia el
    // `transform` en vez de dejar el último valor: si alguien activa la
    // preferencia con la página a media página, el fondo debe volver a su
    // sitio, no congelarse desplazado.
    if (!enabled) {
      layer.style.transform = "";
      layer.style.willChange = "";
      return;
    }

    const host = layer.parentElement;
    if (!host) return;

    layer.style.willChange = "transform";

    let frame = 0;
    const paint = () => {
      frame = 0;
      const { top, height } = host.getBoundingClientRect();
      // Fuera de pantalla no hay nada que mover, y sí un `getBoundingClientRect`
      // que ahorrarse en cada rueda del ratón del resto de la página.
      if (top + height < 0 || top > window.innerHeight) return;
      const shift = Math.min(Math.max(0, -top) * SHIFT, height * OVERSCAN);
      layer.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
    };

    // El listener solo APUNTA el trabajo; quien lo hace es el fotograma.
    // Así, aunque el navegador dispare veinte eventos de scroll entre dos
    // fotogramas, solo se calcula y se escribe una vez.
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(paint);
    };

    paint();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      layer.style.transform = "";
      layer.style.willChange = "";
    };
  }, [enabled]);

  return ref;
}
